import { useMutation, useQuery } from '@tanstack/react-query';

import { queryClient } from '~app/query-client';

import { baseAPI } from '~shared/api/base-api';
import { KEYS } from '~shared/api/query-keys';

export const PAYABLE_INVOICE_BUCKET = {
  PAST_DUE: 'past_due',
  DUE_NOW: 'due_now',
  UPCOMING: 'upcoming',
} as const;

export type PayableInvoiceBucket =
  (typeof PAYABLE_INVOICE_BUCKET)[keyof typeof PAYABLE_INVOICE_BUCKET];

export interface PayableInvoice {
  invoiceId: string;
  studentId: string | null;
  studentName: string;
  description: string;
  status: string;
  bucket: PayableInvoiceBucket;
  dueDate: string | null;
  originalAmount: number;
  remainingBalance: number;
  isPayable: boolean;
  blockedReason: string | null;
}

export interface PayableInvoicesResponse {
  settings: {
    allowSelectiveInvoicePayment: boolean;
    allowPartialInvoicePayment: boolean;
  };
  groups: {
    pastDue: Array<PayableInvoice>;
    dueNow: Array<PayableInvoice>;
    upcoming: Array<PayableInvoice>;
  };
  totals: {
    pastDue: number;
    dueNow: number;
    upcoming: number;
    amountDue: number;
    outstanding: number;
  };
}

export type TakePaymentMethod = 'card' | 'cash' | 'check';

export interface TakePaymentAllocation {
  invoiceId: string;
  /** Omit to apply the charge's full remaining balance. */
  amount?: number;
}

export interface TakePaymentParams {
  parentId: string;
  studentId?: string;
  method: TakePaymentMethod;
  invoices?: Array<TakePaymentAllocation>;
  totalAmount?: number;
  paymentMethodId?: string;
  paymentDate?: string;
  checkNumber?: string;
  note?: string;
  transactionCodeId?: string;
}

export interface TakePaymentPreview {
  allocations: Array<{
    invoiceId: string;
    amount: number;
    description: string;
    studentName: string;
  }>;
  allocatedTotal: number;
  transactionFeeAmount: number;
  chargeTotal: number;
  paymentMethodType: string;
  reallocatedByStudioPolicy: boolean;
}

export interface TakePaymentResponse {
  status: 'success';
  message: string;
  allocations: Array<{ invoiceId: string; amount: number; status?: string }>;
  allocatedTotal: number;
  transactionFeeAmount?: number;
  chargeTotal?: number;
  overpaymentAmount?: number;
  paymentIntentId?: string;
  isProcessing?: boolean;
  reallocatedByStudioPolicy?: boolean;
}

export const getPayableInvoices = async (params: {
  parentId: string;
  studentId?: string;
}) => {
  const response = await baseAPI.get<PayableInvoicesResponse>(
    '/payment-processor/payable-invoices',
    { params },
  );
  return response.data;
};

export const useGetPayableInvoices = (
  params: { parentId: string; studentId?: string },
  options?: { enabled?: boolean },
) => {
  return useQuery({
    queryKey: [KEYS.PAYABLE_INVOICES, params.parentId, params.studentId ?? null],
    queryFn: () => getPayableInvoices(params),
    enabled: (options?.enabled ?? true) && Boolean(params.parentId),
  });
};

export const previewTakePayment = async (params: TakePaymentParams) => {
  const response = await baseAPI.post<TakePaymentPreview>(
    '/payment-processor/take-payment/preview',
    params,
  );
  return response.data;
};

/** Card only: cash and check never touch Stripe, so there is no fee to preview. */
export const useTakePaymentPreview = (
  params: TakePaymentParams,
  options?: { enabled?: boolean },
) => {
  return useQuery({
    queryKey: [KEYS.TAKE_PAYMENT_PREVIEW, params],
    queryFn: () => previewTakePayment(params),
    enabled: (options?.enabled ?? true) && Boolean(params.parentId),
    retry: false,
    staleTime: 0,
  });
};

export const takePayment = async (params: TakePaymentParams) => {
  const response = await baseAPI.post<TakePaymentResponse>(
    '/payment-processor/take-payment',
    params,
  );
  return response.data;
};

export const useTakePaymentMutation = () => {
  return useMutation({
    mutationFn: takePayment,
    onSuccess: (_response, params) => {
      const keysToInvalidate = [
        [KEYS.PAYABLE_INVOICES, params.parentId],
        [KEYS.STUDENT_SUBSCRIPTION_INVOICES, params.studentId],
        [KEYS.OUTSTANDING_BALANCE, params.studentId],
        [KEYS.PARENT_OUTSTANDING_BALANCES],
        [KEYS.PARENT_WALLET_TRANSACTIONS, params.parentId],
        [KEYS.WALLET_BALANCE, params.parentId],
        [KEYS.STUDENT_PAYMENT_HISTORY],
        [KEYS.PARENT_PAYMENTS_HISTORY],
        [KEYS.ACCOUNTS_RECEIVABLE],
        [KEYS.ACCOUNTS_RECEIVABLE_DUE_DATE_GROUPING],
        [KEYS.ACCOUNTS_RECEIVABLE_STUDENT_GROUPING],
      ];

      for (const queryKey of keysToInvalidate) {
        void queryClient.invalidateQueries({ queryKey });
      }
    },
  });
};
